import Flexbox from "$/lib/components/common/Flexbox";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import AreaGraphWidget from "$/lib/components/widgets/AreaGraphWidget";
import BoxIcon from "$/lib/icons/tabs/BoxIcon";
import FirelightIcon from "$/lib/icons/tabs/FirelightIcon";
import LightningIcon from "$/lib/icons/tabs/LightningIcon";
import type { IndicatorType } from "$/lib/types/api/market-monitoring/market-monitoring.types";
import type { ThemeChoices } from "$/lib/types/General.types";
import type { ReactNode } from "@tanstack/react-router";
import MarketMonitoringDatePicker from "./MarketMonitoringDatePicker";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import { cn } from "$/lib/utils/functions/misc.functions";
import type React from "react";

type Props = {
  type: "ELECTRICITY" | "GAS" | "OTHER_MARKETS";
  indicatorValues: IndicatorType[];
  selectedDate?: Date;
  setSelectedDate?: (date: Date) => void;
  hasChart?: boolean;
  children?: ReactNode;
  hasDatePicker?: boolean;
  asMain?: boolean;
  isLoading?: boolean;
  containerClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  itemClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
};

const ProductIndicator = ({
  type,
  indicatorValues,
  selectedDate,
  setSelectedDate,
  hasChart = true,
  hasDatePicker = true,
  asMain = true,
  children,
  isLoading,
  containerClassName,
  itemClassName,
}: Props) => {
  const productType: {
    [key in "ELECTRICITY" | "GAS" | "OTHER_MARKETS"]: {
      icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
      title: string;
      color: ThemeChoices;
      items: IndicatorType[];
    };
  } = {
    ELECTRICITY: {
      icon: LightningIcon,
      color: "blue",
      title: "Indicateur Electricité",
      items: indicatorValues,
    },
    GAS: {
      icon: FirelightIcon,
      title: "Indicateur Gaz naturel",
      color: "green",
      items: indicatorValues,
    },
    OTHER_MARKETS: {
      icon: BoxIcon,
      title: "Indicateur Autres marchés",
      color: "orange",
      items: indicatorValues,
    },
  };

  const { icon: Icon, title, color, items } = productType[type];

  return (
    <Flexbox
      fullWidth
      align="stretch"
      className={cn(
        "h-full gap-3 rounded-3xl bg-primary-200 p-4",
        containerClassName,
      )}
    >
      {hasDatePicker && setSelectedDate && selectedDate && (
        <MarketMonitoringDatePicker
          theme={color}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      )}

      {children}

      {isLoading ? (
        <Flexbox fullWidth align="center" justify="center">
          <Loader height={40} width={40} />
        </Flexbox>
      ) : asMain ? (
        <DisplayBox title={title} Icon={Icon} theme={color}>
          <Flexbox
            row
            align="stretch"
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
            }}
          >
            {items.map((item, index) => (
              <div
                key={index}
                className={cn(
                  "min-w-44 grow rounded-3xl bg-white p-4 shadow-sm drop-shadow-md",
                  itemClassName,
                )}
              >
                <AreaGraphWidget
                  theme={color}
                  indicator={item}
                  hasChart={hasChart}
                  asMain={asMain}
                />
              </div>
            ))}
          </Flexbox>
        </DisplayBox>
      ) : (
        <Flexbox row align="center" className="gap-2">
          {items.map((item, index) => (
            <div
              key={index}
              className={cn(
                "min-w-44 grow rounded-3xl bg-white p-4 shadow-sm drop-shadow-md",
                itemClassName,
              )}
            >
              <AreaGraphWidget
                theme={color}
                indicator={item}
                hasChart={hasChart}
                asMain={false}
              />
            </div>
          ))}
        </Flexbox>
      )}

      {}
    </Flexbox>
  );
};

export default ProductIndicator;
